import { FC } from "react";
import { Flex } from "@radix-ui/themes";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/button";
import { toastModule } from "@/modules/toast";

import { DataExport, fetchDataExportDownloadUrl, runDataExport, triggerZipGeneration } from "./api";

interface DataExportActionsProps {
  dataExport: DataExport;
  canDownload: boolean;
}

const describeError = (error: unknown) =>
  error instanceof Error ? error.message : "Something went wrong.";

export const DataExportActions: FC<DataExportActionsProps> = ({ dataExport, canDownload }) => {
  const queryClient = useQueryClient();

  const runExportMutation = useMutation({
    mutationFn: () => runDataExport(dataExport.id),
    onSuccess: response => {
      queryClient.invalidateQueries({ queryKey: ["data-export", dataExport.id] });
      toastModule.triggerToast({
        content: `Export workflow queued: ${response?.workflow_id ?? "started"}.`,
      });
    },
    onError: error => {
      toastModule.triggerToast({
        content: `Failed to queue export workflow: ${describeError(error)}`,
      });
    },
  });

  const zipMutation = useMutation({
    mutationFn: () => triggerZipGeneration(dataExport.id),
    onSuccess: response => {
      queryClient.invalidateQueries({ queryKey: ["data-export", dataExport.id] });
      toastModule.triggerToast({
        content: `Zip generation started: ${response?.workflow_id ?? "started"}.`,
      });
    },
    onError: error => {
      toastModule.triggerToast({
        content: `Failed to start zip generation: ${describeError(error)}`,
      });
    },
  });

  const downloadMutation = useMutation({
    mutationFn: () => fetchDataExportDownloadUrl(dataExport.id),
    onSuccess: response => {
      if (!response?.url) {
        toastModule.triggerToast({ content: "Download URL unavailable." });
        return;
      }

      toastModule.triggerToast({ content: "Download starting…" });
      window.open(response.url, "_blank", "noopener,noreferrer");
    },
    onError: error => {
      toastModule.triggerToast({
        content: `Failed to fetch download URL: ${describeError(error)}`,
      });
    },
  });

  return (
    <Flex gap="2" wrap="wrap">
      <Button disabled={runExportMutation.isPending} onClick={() => runExportMutation.mutate()}>
        {runExportMutation.isPending ? "Starting…" : "Run Export Workflow"}
      </Button>
      <Button disabled={zipMutation.isPending} onClick={() => zipMutation.mutate()}>
        {zipMutation.isPending ? "Triggering…" : "Generate Zip Only"}
      </Button>
      <Button
        variant="soft"
        disabled={!canDownload || downloadMutation.isPending}
        onClick={() => downloadMutation.mutate()}
        title={!canDownload ? "Zip path not available yet." : undefined}
      >
        {downloadMutation.isPending ? "Preparing…" : "Download Zip"}
      </Button>
    </Flex>
  );
};
